// Pure logic for the multi-view prize-product print-zone editor.
//
// The visual editor is a native port of product-config-admin's MockupEditor
// (product-config-admin/src/App.jsx:275 MockupEditor, :41 ZONE_VIEWS_UI,
// :191 blank()). Everything here is I/O-free and DOM-free so it can be unit
// tested — the client has no jsdom, so components stay dumb and the math,
// grouping and guard rails live in this file.
//
// STORED SHAPE — unchanged from what productconfig/lib/decoration.js
// derivePrintAreas() already reads. This module never invents a field:
//   printAreas:  { CODE: { widthInches, heightInches, mockup: { view, x, y,
//                          maxW, maxH }, font?, maxChars?, maxDigits?, ink? } }
//   mockupViews: { view: { baseImage, overlayImage? } }   // GCS filenames
//
// Two coordinate spaces, exactly as in the PCA:
//   NATIVE  — the mockup image's own pixels. THIS is what is stored and what
//             the proof renderer composites against.
//   SCREEN  — the on-screen size of the <img>, which is capped by CSS.
// scale = displayedWidth / naturalWidth; native * scale = screen.
import {
  type PrintArea, type PrintAreaMockup, type MockupView, type MockupViewName,
  type DecorationLocation, type DecorationRegistry,
  VALID_VIEWS, VIEW_LABELS, sortedLocations, isTextCapable, conflictsFor, conflictingPairs,
} from './prize-product';

export type Nat = { w: number; h: number };
export type Box = { x: number; y: number; maxW: number; maxH: number };

// The four garment sides an operator uploads, and their labels, both come from
// prize-product.ts. Re-exported so existing import sites keep working.
export { VIEW_LABELS };

// Smallest box the operator can shrink a zone to, in NATIVE px. Mirrors
// ViewZoneCanvas MIN_ZONE (the PCA uses 20 against un-scaled coords; 12 is the
// value the console's team-store canvas already ships, kept identical so both
// canvases behave the same).
export const MIN_ZONE = 12;

// NO FALLBACK IMAGE SIZE (2026-08-11). The PCA falls back to 600×800 when an
// image has not reported its natural size (App.jsx:294 img.onerror) and this
// module used to copy that. It is the one PCA behaviour we must NOT port: a box
// laid out against a fabricated canvas is stored in that canvas's pixels, and
// nothing downstream can tell it apart from a measured one — the server only
// checks the numbers are positive, so the decoration lands wherever 600×800
// happens to map to on the real image (or off it entirely) on the proof AND on
// the print file. Every entry point that produces geometry therefore demands
// the measured size and fails loud without it; see addZoneDisabledReason for
// the operator-facing half of the same rule.

// Stable zone tints, reused from ViewZoneCanvas so both editors look alike.
export const ZONE_COLORS = ['#007aff', '#34c759', '#ff9500', '#af52de', '#ff2d55', '#5ac8fa'];

export function zoneColor(codes: string[], code: string): string {
  const idx = codes.indexOf(code);
  return ZONE_COLORS[(idx < 0 ? 0 : idx) % ZONE_COLORS.length];
}

// ---------------------------------------------------------------------------
// Screen ↔ native pixel conversion (PCA App.jsx:310-316)
// ---------------------------------------------------------------------------

/** scale such that `native * scale = screen`. Falls back to 1 before load. */
export function scaleFor(nat: Nat | null, displayedWidth: number): number {
  if (!nat || !nat.w || !displayedWidth) return 1;
  return displayedWidth / nat.w;
}

/** screen px → native px. Rounded: stored coordinates are whole pixels. */
export const toNative = (px: number, scale: number): number => Math.round(px / scale);

/** native px → screen px. NOT rounded — sub-pixel CSS keeps the box smooth. */
export const toScreen = (px: number, scale: number): number => px * scale;

/** Keep a box inside the image and above MIN_ZONE (PCA onMove clamping). */
export function clampBox(b: Box, nat: Nat): Box {
  const maxW = Math.min(Math.max(MIN_ZONE, Math.round(b.maxW)), nat.w);
  const maxH = Math.min(Math.max(MIN_ZONE, Math.round(b.maxH)), nat.h);
  return {
    maxW,
    maxH,
    x: Math.min(Math.max(0, Math.round(b.x)), nat.w - maxW),
    y: Math.min(Math.max(0, Math.round(b.y)), nat.h - maxH),
  };
}

// ---------------------------------------------------------------------------
// Keyboard placement (Codex F11, 2026-08-11)
//
// The zone boxes and their resize handles were pointer-only <div>s: no tab stop,
// no role, no key handling. Dragging a box with a mouse was the ONLY way to
// place a decoration, which puts the print position of every prize product out
// of reach of anyone who cannot use a pointer — and makes exact placement hard
// for everyone, because a 1px nudge by drag is luck. Arrow keys move, Shift +
// arrows resize, Alt takes a coarse step. The numeric x/y/w/h inputs in the
// editor are the third path: type the number when you know it.
// ---------------------------------------------------------------------------

export const NUDGE_STEP = 1;
export const NUDGE_STEP_COARSE = 10;

export type NudgeModifiers = { shift?: boolean; alt?: boolean };

/**
 * The box after one arrow-key press, or null when the key is not one we handle
 * (so the caller leaves the event alone instead of swallowing Tab/Enter).
 * Clamped exactly like a drag — keyboard and pointer must not be able to produce
 * geometry the other could not.
 */
export function nudgeBox(
  box: Box,
  nat: Nat,
  key: string,
  mods: NudgeModifiers = {},
): Box | null {
  const step = mods.alt ? NUDGE_STEP_COARSE : NUDGE_STEP;
  let dx = 0;
  let dy = 0;
  if (key === 'ArrowLeft') dx = -step;
  else if (key === 'ArrowRight') dx = step;
  else if (key === 'ArrowUp') dy = -step;
  else if (key === 'ArrowDown') dy = step;
  else return null;
  const next: Box = mods.shift
    ? { ...box, maxW: box.maxW + dx, maxH: box.maxH + dy }
    : { ...box, x: box.x + dx, y: box.y + dy };
  return clampBox(next, nat);
}

/** The screen-reader sentence for one zone box. */
export function zoneBoxLabel(code: string, box: Box, nat: Nat | null): string {
  const where = `at ${box.x}, ${box.y}, ${box.maxW} by ${box.maxH} pixels`;
  const of = nat ? ` in a ${nat.w} by ${nat.h} image` : '';
  return `${code} print zone, ${where}${of}. Arrow keys move, shift plus arrow keys resize.`;
}

// ---------------------------------------------------------------------------
// Registry-driven code lists — nothing about FF/LC/RC/UB is hardcoded here.
// The four standard codes carry `standard: true` in
// platformConfig/decorationLocations; this module only knows "standard first".
// ---------------------------------------------------------------------------

/** Standard locations first, then registry sort order within each group. */
export function standardFirst(locs: DecorationLocation[]): DecorationLocation[] {
  return [...locs].sort(
    (a, b) => Number(b.standard === true) - Number(a.standard === true) || a.sort - b.sort,
  );
}

/** Active locations that decorate `view`, standard-first. */
export function locationsForView(
  reg: DecorationRegistry | undefined,
  view: MockupViewName,
): DecorationLocation[] {
  return standardFirst(sortedLocations(reg).filter((l) => l.view === view));
}

/** locationsForView minus codes the product already uses (dedupe by filtering). */
export function addableLocations(
  reg: DecorationRegistry | undefined,
  view: MockupViewName,
  printAreas: Record<string, PrintArea>,
): DecorationLocation[] {
  return locationsForView(reg, view).filter((l) => !printAreas[l.code]);
}

// ---------------------------------------------------------------------------
// Mockup image load state (Codex F10, 2026-08-11)
//
// The canvas used to handle onLoad and nothing else. A base image that 404s —
// deleted from the bucket, renamed, a filename typed into the picker that no
// longer resolves — therefore left the editor saying "Waiting for the … image to
// load" FOREVER, with no way to tell a slow image from a dead one. Worse, the
// save gate (zonesMissingImage) only asks whether a filename STRING is present,
// so the operator could save a product whose zones point at an image the proof
// renderer will not be able to fetch either.
//
// 'loading' and 'failed' are therefore separate states, and 'failed' blocks the
// save exactly the way a missing filename does.
// ---------------------------------------------------------------------------

export type ImageLoadState = 'loading' | 'loaded' | 'failed';
export type ViewImageStates = Partial<Record<MockupViewName, ImageLoadState>>;

/**
 * Zones whose view HAS a filename but whose image could not be fetched.
 * Disjoint from zonesMissingImage by construction: that one is about a blank
 * field, this one is about a name that does not resolve.
 */
export function zonesWithBrokenImage(
  printAreas: Record<string, PrintArea>,
  mockupViews: Record<string, MockupView>,
  states: ViewImageStates,
): MissingImage[] {
  const out: MissingImage[] = [];
  for (const [code, area] of Object.entries(printAreas || {})) {
    const view = area?.mockup?.view;
    if (!view) continue;
    const entry = (mockupViews || {})[view];
    if (!entry || !String(entry.baseImage || '').trim()) continue; // zonesMissingImage owns this
    if ((states || {})[view] === 'failed') out.push({ code, view });
  }
  return out;
}

/**
 * Why the "Add zone" control cannot be used on this view right now, or null when
 * it can. Every reason is a SENTENCE the operator reads, not a disabled button
 * they have to guess at: the editor renders this string in place of the form.
 *
 * The measured-image check sits alongside the registry check on purpose. Both
 * are inputs the editor cannot fabricate — an unknown location code and an
 * unknown canvas size are the same class of missing fact, and the second one is
 * the more dangerous because a guessed canvas still produces numbers that look
 * exactly like real coordinates once they are saved.
 *
 * FAILED is checked BEFORE "still loading": they are the same missing fact but
 * they need opposite things from the operator — one is "wait", the other is
 * "this will never arrive, retry or replace it".
 */
export function addZoneDisabledReason({
  registry, view, hasImage, nat, addableCount, imageState,
}: {
  registry: DecorationRegistry | undefined;
  view: MockupViewName;
  hasImage: boolean;
  nat: Nat | null;
  addableCount: number;
  imageState?: ImageLoadState;
}): string | null {
  const label = VIEW_LABELS[view];
  if (!registry) return 'Decoration locations are still loading.';
  if (!hasImage) {
    return `Upload a ${label} image first — a zone's position is stored in that image's pixels, `
      + 'so there is nothing to place it against yet.';
  }
  if (imageState === 'failed') {
    return `The ${label} image could not be loaded, so there is nothing to measure a zone against. `
      + 'Retry it, or replace it with an image that loads — waiting will not help.';
  }
  if (!nat || !(nat.w > 0) || !(nat.h > 0)) {
    return `Waiting for the ${label} image to load. A zone's position is measured in that image's `
      + 'pixels, so adding one before it loads would save a guessed position — and the decoration '
      + 'would print in the wrong place.';
  }
  if (addableCount === 0) return `Every ${label} location is already in use.`;
  return null;
}

// ---------------------------------------------------------------------------
// view → zone grouping
// ---------------------------------------------------------------------------

export type ViewGroups = Record<MockupViewName, string[]>;

const emptyGroups = (): ViewGroups => ({
  front: [], back: [], 'sleeve-left': [], 'sleeve-right': [],
});

/**
 * Codes placed on each view, keyed by the area's OWN `mockup.view` (not the
 * registry's default view — an operator may place a code on a different side,
 * and the renderer follows mockup.view).
 */
export function groupAreasByView(printAreas: Record<string, PrintArea>): ViewGroups {
  const out = emptyGroups();
  for (const [code, area] of Object.entries(printAreas || {})) {
    const view = area?.mockup?.view;
    if (view && VALID_VIEWS.includes(view)) out[view].push(code);
  }
  return out;
}

/** Codes with no mockup block at all — they exist but cannot be drawn. */
export function unplacedCodes(printAreas: Record<string, PrintArea>): string[] {
  return Object.entries(printAreas || {})
    .filter(([, a]) => !a?.mockup?.view)
    .map(([code]) => code);
}

/** Views any zone points at — the set whose images may not be removed. */
export function viewsInUse(printAreas: Record<string, PrintArea>): MockupViewName[] {
  const groups = groupAreasByView(printAreas);
  return VALID_VIEWS.filter((v) => groups[v].length > 0);
}

// ---------------------------------------------------------------------------
// Zone add / remove / edit — every function returns a NEW record.
// ---------------------------------------------------------------------------

export type NewZone = {
  code: string;
  view: MockupViewName;
  widthInches: number;
  heightInches: number;
  font?: string;
  maxChars?: number;
  maxDigits?: number;
  ink?: string;
};

/** The measured natural size, or a thrown error naming why we won't guess. */
export function requireNat(nat: Nat | null | undefined): Nat {
  if (!nat || !(nat.w > 0) || !(nat.h > 0)) {
    throw new Error(
      'The mockup image has not reported its size yet. Zone coordinates are stored in that '
      + "image's own pixels, so placing one now would measure it against a guess and print the "
      + 'decoration in the wrong spot. Wait for the image to load (or upload one), then try again.',
    );
  }
  return nat;
}

/**
 * A visible, draggable starting box for a brand-new zone: 30% of the image
 * width, height derived from the INCH aspect ratio so what the operator sees
 * already matches the print they asked for, centred, then cascaded by 24px per
 * zone already on that view so two zones never land exactly on top of one
 * another.
 *
 * THROWS when the image's natural size is unknown — see the no-fallback note at
 * the top of this file. A caller that cannot measure the image has nothing to
 * lay a box out in, and a guessed box is indistinguishable from a real one once
 * it is saved.
 */
export function defaultBoxForZone(
  nat: Nat | null,
  widthInches: number,
  heightInches: number,
  siblingCount = 0,
): Box {
  const base = requireNat(nat);
  const aspect = Number.isFinite(widthInches) && Number.isFinite(heightInches)
    && widthInches > 0 && heightInches > 0
    ? heightInches / widthInches
    : 1;
  let w = Math.round(base.w * 0.3);
  let h = Math.round(w * aspect);
  const hCap = Math.round(base.h * 0.6);
  if (h > hCap) { h = hCap; w = Math.round(h / aspect); }
  const offset = Math.max(0, siblingCount) * 24;
  return clampBox(
    { x: Math.round((base.w - w) / 2) + offset, y: Math.round((base.h - h) / 2) + offset, maxW: w, maxH: h },
    base,
  );
}

/**
 * Add a zone. Duplicate codes throw — the UI filters used codes out of the
 * picker, so reaching this is a bug, and a silent overwrite would erase the
 * operator's existing placement.
 *
 * `nat` has NO default: the measured image size is an argument the caller must
 * have, not one this module can invent. Passing null throws (defaultBoxForZone).
 */
export function addZone(
  printAreas: Record<string, PrintArea>,
  zone: NewZone,
  nat: Nat | null,
): Record<string, PrintArea> {
  const areas = printAreas || {};
  if (areas[zone.code]) throw new Error(`Print area ${zone.code} already exists on this product.`);
  if (!VALID_VIEWS.includes(zone.view)) throw new Error(`Unknown mockup view "${zone.view}".`);
  if (!(zone.widthInches > 0) || !(zone.heightInches > 0)) {
    throw new Error(`Print size for ${zone.code} must be greater than 0 inches.`);
  }
  const siblings = groupAreasByView(areas)[zone.view].length;
  const box = defaultBoxForZone(nat, zone.widthInches, zone.heightInches, siblings);
  const area: PrintArea = {
    widthInches: zone.widthInches,
    heightInches: zone.heightInches,
    mockup: { view: zone.view, ...box },
  };
  if (zone.font) area.font = zone.font;
  if (Number.isFinite(zone.maxChars as number)) area.maxChars = zone.maxChars;
  if (Number.isFinite(zone.maxDigits as number)) area.maxDigits = zone.maxDigits;
  if (zone.ink) area.ink = zone.ink;
  return { ...areas, [zone.code]: area };
}

export function removeZone(
  printAreas: Record<string, PrintArea>,
  code: string,
): Record<string, PrintArea> {
  const out: Record<string, PrintArea> = {};
  for (const [c, a] of Object.entries(printAreas || {})) if (c !== code) out[c] = a;
  return out;
}

/** Move/resize a zone. Touches ONLY mockup.{x,y,maxW,maxH} — inches are the
 *  print size of record and are independent of the on-image box. */
export function setZoneBox(
  printAreas: Record<string, PrintArea>,
  code: string,
  box: Box,
): Record<string, PrintArea> {
  const area = (printAreas || {})[code];
  if (!area) throw new Error(`No print area ${code} to place.`);
  const view = area.mockup?.view;
  if (!view) throw new Error(`printAreas["${code}"] has no mockup.view to place against.`);
  const mockup: PrintAreaMockup = { view, x: box.x, y: box.y, maxW: box.maxW, maxH: box.maxH };
  return { ...printAreas, [code]: { ...area, mockup } };
}

/** Set the PRINT size in inches. Never touches the mockup box. */
export function setZoneInches(
  printAreas: Record<string, PrintArea>,
  code: string,
  inches: { widthInches?: number; heightInches?: number },
): Record<string, PrintArea> {
  const area = (printAreas || {})[code];
  if (!area) throw new Error(`No print area ${code} to size.`);
  const next: PrintArea = { ...area };
  if (inches.widthInches !== undefined) next.widthInches = inches.widthInches;
  if (inches.heightInches !== undefined) next.heightInches = inches.heightInches;
  return { ...printAreas, [code]: next };
}

/** Set/clear a text-decoration field (font, maxChars, maxDigits, ink). An
 *  empty value DELETES the key — the server rejects `font: ''`. */
export function setZoneField(
  printAreas: Record<string, PrintArea>,
  code: string,
  key: 'font' | 'maxChars' | 'maxDigits' | 'ink',
  value: string | number | undefined,
): Record<string, PrintArea> {
  const area = (printAreas || {})[code];
  if (!area) throw new Error(`No print area ${code} to edit.`);
  const next: PrintArea = { ...area };
  if (value === undefined || value === '' || (typeof value === 'number' && !Number.isFinite(value))) {
    delete next[key];
  } else {
    (next as Record<string, unknown>)[key] = value;
  }
  return { ...printAreas, [code]: next };
}

/** Move a zone to a different garment side, keeping its box and print size. */
export function setZoneView(
  printAreas: Record<string, PrintArea>,
  code: string,
  view: MockupViewName,
): Record<string, PrintArea> {
  const area = (printAreas || {})[code];
  if (!area) throw new Error(`No print area ${code} to move.`);
  if (!VALID_VIEWS.includes(view)) throw new Error(`Unknown mockup view "${view}".`);
  const m = area.mockup;
  return {
    ...printAreas,
    [code]: { ...area, mockup: { view, x: m?.x ?? 0, y: m?.y ?? 0, maxW: m?.maxW ?? 100, maxH: m?.maxH ?? 100 } },
  };
}

// ---------------------------------------------------------------------------
// Guard rails
// ---------------------------------------------------------------------------

export type MissingImage = { code: string; view: MockupViewName };

/**
 * Zones whose view has no uploaded base image. The proof renderer would
 * composite onto nothing, so the dialog must block the save on this BEFORE the
 * round trip — the server enforces the same rule (lib/prize-product.js
 * checkArea F14 / productconfig assertMockupViews), this is the early surface.
 */
export function zonesMissingImage(
  printAreas: Record<string, PrintArea>,
  mockupViews: Record<string, MockupView>,
): MissingImage[] {
  const out: MissingImage[] = [];
  for (const [code, area] of Object.entries(printAreas || {})) {
    const view = area?.mockup?.view;
    if (!view) continue;
    const entry = (mockupViews || {})[view];
    if (!entry || !String(entry.baseImage || '').trim()) out.push({ code, view });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Zone conflicts (Grant 2026-08-11)
//
// Defining BOTH sides of a conflict on one product is CORRECT and normal — all
// our tops offer Full Front and both chest logos, and the operator picks per
// order, not per garment. So the editor says so in passing and does not block.
//
// What it DOES block is two zones that could be ordered together sitting on top
// of one another. Browser mirror of ../../../lib/prize-product.js
// overlappingZones (copy-not-import) so the operator sees it in the editor
// rather than as a 400 after the round trip.
// ---------------------------------------------------------------------------

/**
 * The passing note for a zone that excludes others, or null when it excludes
 * nothing. Returns codes only — the caller has the registry for labels.
 */
export function conflictNote(
  reg: DecorationRegistry | undefined,
  code: string,
): string | null {
  if (!reg?.locations?.[code]) return null;
  const others = conflictsFor(reg, code);
  if (!others.length) return null;
  return `conflicts with ${others.join(', ')} — a submission uses one or the other`;
}

export type ZoneOverlap = { a: string; b: string; view: MockupViewName };

const zoneRect = (area: PrintArea | undefined) => {
  const m = area?.mockup;
  if (!m?.view) return null;
  if (![m.x, m.y, m.maxW, m.maxH].every((n) => Number.isFinite(n))) return null;
  return { view: m.view, x: m.x, y: m.y, w: m.maxW, h: m.maxH };
};

/**
 * Overlapping zone pairs that are NOT a conflicting pair — the ones that really
 * could print on top of each other. Sorted, so the message is stable.
 * Unknown codes are SKIPPED here rather than thrown: the editor already flags
 * them in red per zone ("not in the decoration registry"), and throwing would
 * blank the whole panel instead of showing that.
 */
export function overlappingZones(
  printAreas: Record<string, PrintArea>,
  reg: DecorationRegistry | undefined,
): ZoneOverlap[] {
  if (!reg?.locations) return [];
  const codes = Object.keys(printAreas || {})
    .filter((code) => !!reg.locations[code] && !!zoneRect(printAreas[code]))
    .sort();
  const conflicting = new Set(conflictingPairs(reg, codes).map(([a, b]) => `${a}|${b}`));
  const out: ZoneOverlap[] = [];
  for (let i = 0; i < codes.length; i += 1) {
    for (let j = i + 1; j < codes.length; j += 1) {
      const a = codes[i];
      const b = codes[j];
      if (conflicting.has(`${a}|${b}`)) continue;
      const ra = zoneRect(printAreas[a])!;
      const rb = zoneRect(printAreas[b])!;
      if (ra.view === rb.view
        && ra.x < rb.x + rb.w && rb.x < ra.x + ra.w
        && ra.y < rb.y + rb.h && rb.y < ra.y + ra.h) {
        out.push({ a, b, view: ra.view });
      }
    }
  }
  return out;
}

/** Codes that block clearing `view`'s image. Empty ⇒ removal is safe. */
export function codesBlockingViewRemoval(
  printAreas: Record<string, PrintArea>,
  view: MockupViewName,
): string[] {
  return groupAreasByView(printAreas)[view] || [];
}

/** Set a view's images. Passing baseImage '' clears the whole view entry. */
export function setViewImage(
  mockupViews: Record<string, MockupView>,
  view: MockupViewName,
  patch: { baseImage?: string; overlayImage?: string },
): Record<string, MockupView> {
  const views = mockupViews || {};
  const current = views[view] || { baseImage: '' };
  const next: MockupView = { ...current };
  if (patch.baseImage !== undefined) next.baseImage = patch.baseImage;
  if (patch.overlayImage !== undefined) {
    if (patch.overlayImage) next.overlayImage = patch.overlayImage;
    else delete next.overlayImage;
  }
  if (!String(next.baseImage || '').trim()) {
    const out: Record<string, MockupView> = {};
    for (const [v, entry] of Object.entries(views)) if (v !== view) out[v] = entry;
    return out;
  }
  return { ...views, [view]: next };
}

// ---------------------------------------------------------------------------
// Editor bootstrap
// ---------------------------------------------------------------------------

/**
 * Which tab to open on. Lands the operator on the first view that already
 * carries a zone (so "Convert to print areas" drops them straight onto the
 * seeded zone), else the first view with an image, else Front.
 */
export function initialView(
  printAreas: Record<string, PrintArea>,
  mockupViews: Record<string, MockupView>,
): MockupViewName {
  const groups = groupAreasByView(printAreas);
  return VALID_VIEWS.find((v) => groups[v].length > 0)
    ?? VALID_VIEWS.find((v) => !!(mockupViews || {})[v]?.baseImage)
    ?? 'front';
}

/** Label for the "Add zone" picker: `FF — Full Front · standard`. */
export function locationOptionLabel(loc: DecorationLocation): string {
  const kinds = loc.kinds.join('/');
  return `${loc.code} — ${loc.label} (${kinds})${loc.standard === true ? ' · standard' : ''}`;
}

export { isTextCapable };
